import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Car, Clock, Edit, Star, Package, Navigation, User, Mail, Phone, CreditCard, Bike, TrendingUp, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import DriverMap from '@/components/DriverMap';
import DriverBottomNav, { DriverTab } from '@/components/driver/DriverBottomNav';
import DeliveryOfferPopup, { DeliveryOffer } from '@/components/driver/DeliveryOfferPopup';
import { calculateOnTimeRate } from '@/utils/deliveryMetrics';
import { isRideDriverEmail } from '@/config/features';
import { useDriverOrderNotifications } from '@/hooks/useDriverOrderNotifications';

interface Order {
  id: string;
  customer_id: string;
  restaurant_id: string;
  driver_id: string | null;
  status: string;
  delivery_address: string;
  delivery_type: string;
  created_at: string;
  customer_name: string;
  customer_phone: string;
  notes: string | null;
  restaurants: {
    name: string;
    address: string;
  } | null;
  total_amount: number;
  delivery_fee: number;
  payment_method?: string | null;
  payment_status?: string | null;
}

interface DriverProfile {
  full_name: string;
  email: string;
  phone_number: string;
  license_number: string;
  vehicle_type: string;
}

interface DriverRatings {
  averageRating: number;
  totalRatings: number;
}

const startOfWeek = () => {
  const now = new Date();
  const day = now.getUTCDay(); // 0 = Sunday
  const diff = day === 0 ? 6 : day - 1; // week starts Monday
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - diff));
  return start.toISOString();
};

const DriverDashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState<DriverTab>('drive');
  const [isOnline, setIsOnline] = useState(false);
  const [isFreelance, setIsFreelance] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [queuedOrders, setQueuedOrders] = useState<Order[]>([]);
  const [offers, setOffers] = useState<Order[]>([]);
  const [declinedIds, setDeclinedIds] = useState<string[]>([]);
  const [acceptedIds, setAcceptedIds] = useState<string[]>([]);
  const [offerItems, setOfferItems] = useState<Record<string, { name: string; quantity: number }[]>>({});
  const [accepting, setAccepting] = useState(false);

  const [showOrderCard, setShowOrderCard] = useState(true);
  const [todayStats, setTodayStats] = useState({ deliveries: 0, onTimeRate: 95 });
  const [weekStats, setWeekStats] = useState({ earnings: 0, deliveries: 0 });
  const [dailyWeek, setDailyWeek] = useState<{ label: string; earnings: number; deliveries: number }[]>([]);
  const [pastWeeks, setPastWeeks] = useState<{ label: string; earnings: number; deliveries: number }[]>([]);
  const [driverProfile, setDriverProfile] = useState<DriverProfile | null>(null);
  const [driverRatings, setDriverRatings] = useState<DriverRatings>({ averageRating: 0, totalRatings: 0 });
  const [driverLocation, setDriverLocation] = useState<{ lat: number; lng: number } | null>(null);

  useDriverOrderNotifications(user?.id || '');

  // ---------- Location tracking ----------
  useEffect(() => {
    if (!isOnline || !user || !navigator.geolocation) return;

    let watchId: number | null = null;
    let wakeLock: any = null;
    let lastPos: { lat: number; lng: number; t: number } | null = null;
    let lastWriteAt = 0;
    let fallbackTimer: number | null = null;

    const writeLocation = async (lat: number, lng: number) => {
      try {
        await supabase.from('driver_profiles').upsert({
          user_id: user.id,
          current_latitude: lat,
          current_longitude: lng,
          last_location_update: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      } catch (err) {
        console.error('Error updating location:', err);
      }
    };

    const handlePosition = (position: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = position.coords;
      if (accuracy && accuracy > 100) return;

      const now = Date.now();
      if (lastPos) {
        const dt = (now - lastPos.t) / 1000;
        const R = 6371000;
        const dLat = ((latitude - lastPos.lat) * Math.PI) / 180;
        const dLng = ((longitude - lastPos.lng) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((lastPos.lat * Math.PI) / 180) *
            Math.cos((latitude * Math.PI) / 180) *
            Math.sin(dLng / 2) ** 2;
        const dist = 2 * R * Math.asin(Math.sqrt(a));
        if (dt > 0 && dist / dt > 42) return;
      }

      lastPos = { lat: latitude, lng: longitude, t: now };
      setDriverLocation({ lat: latitude, lng: longitude });

      if (now - lastWriteAt >= 4000) {
        lastWriteAt = now;
        writeLocation(latitude, longitude);
      }
    };

    const options: PositionOptions = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };

    watchId = navigator.geolocation.watchPosition(
      handlePosition,
      (error) => console.error('Location error:', error),
      options,
    );

    fallbackTimer = window.setInterval(() => {
      navigator.geolocation.getCurrentPosition(handlePosition, () => {}, options);
    }, 15000);

    (async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    })();

    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        try {
          if ('wakeLock' in navigator && !wakeLock) {
            wakeLock = await (navigator as any).wakeLock.request('screen');
          }
        } catch { /* ignore */ }
        navigator.geolocation.getCurrentPosition(handlePosition, () => {}, options);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (fallbackTimer) clearInterval(fallbackTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wakeLock) {
        try { wakeLock.release(); } catch { /* ignore */ }
      }
    };
  }, [isOnline, user]);

  // ---------- Auth guard ----------
  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
    if (!authLoading && user && isRideDriverEmail(user.email)) navigate('/ride-driver-dashboard');
  }, [user, authLoading, navigate]);

  // ---------- Data loaders ----------
  const loadDriverStatus = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('driver_profiles')
      .select('is_active, is_freelance')
      .eq('user_id', user.id)
      .maybeSingle();
    if (data) {
      setIsOnline((data as any).is_active || false);
      setIsFreelance(Boolean((data as any).is_freelance));
    }
  }, [user]);

  const loadOrderItems = useCallback(async (orderIds: string[]) => {
    if (orderIds.length === 0) return;
    const { data } = await supabase
      .from('order_items')
      .select('order_id, name, quantity')
      .in('order_id', orderIds);
    if (!data) return;
    const grouped: Record<string, { name: string; quantity: number }[]> = {};
    data.forEach((row: any) => {
      if (!grouped[row.order_id]) grouped[row.order_id] = [];
      grouped[row.order_id].push({ name: row.name || 'Item', quantity: Number(row.quantity || 1) });
    });
    setOfferItems((prev) => ({ ...prev, ...grouped }));
  }, []);

  const fetchDriverOrders = useCallback(async () => {
    if (!user) return;
    const { data: driverOrders, error } = await supabase
      .from('orders')
      .select(`*, restaurants ( name, address )`)
      .eq('driver_id', user.id)
      .eq('delivery_type', 'delivery')
      .in('status', ['confirmed', 'preparing', 'ready_for_pickup', 'picked_up'])
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching driver orders:', error);
      return;
    }

    if (driverOrders && driverOrders.length > 0) {
      const activeOrder = (driverOrders.find((o: any) => o.status === 'picked_up') || driverOrders[0]) as any;
      setCurrentOrder(activeOrder as Order);
      setQueuedOrders(driverOrders.filter((o: any) => o.id !== activeOrder.id) as Order[]);
      loadOrderItems(driverOrders.map((o: any) => o.id));
    } else {
      setCurrentOrder(null);
      setQueuedOrders([]);
    }
  }, [user, loadOrderItems]);

  // Freelance drivers see unassigned offers
  const fetchOffers = useCallback(async () => {
    if (!user || !isFreelance || !isOnline) {
      setOffers([]);
      return;
    }
    const { data } = await supabase
      .from('orders')
      .select(`*, restaurants ( name, address )`)
      .is('driver_id', null)
      .eq('delivery_type', 'delivery')
      .in('status', ['confirmed', 'ready_for_pickup'])
      .order('created_at', { ascending: true })
      .limit(10);
    const rows = (data as any as Order[]) || [];
    setOffers(rows);
    loadOrderItems(rows.map((o) => o.id));
  }, [user, isFreelance, isOnline, loadOrderItems]);


  const fetchTodayStats = useCallback(async () => {
    if (!user) return;
    try {
      const metrics = await calculateOnTimeRate(user.id);
      setTodayStats({ deliveries: metrics.totalDeliveries, onTimeRate: metrics.onTimeRate });
    } catch {
      setTodayStats({ deliveries: 0, onTimeRate: 95 });
    }
  }, [user]);

  const fetchWeekStats = useCallback(async () => {
    if (!user) return;
    // Fetch last 8 weeks of delivered orders so we can build the earnings history
    const WEEKS = 8;
    const now = new Date();
    const day = now.getUTCDay();
    const diff = day === 0 ? 6 : day - 1;
    const currentWeekStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - diff));
    const rangeStart = new Date(currentWeekStart);
    rangeStart.setUTCDate(rangeStart.getUTCDate() - (WEEKS - 1) * 7);

    const { data } = await supabase
      .from('orders')
      .select('delivery_fee, completed_at, created_at')
      .eq('driver_id', user.id)
      .eq('delivery_type', 'delivery')
      .eq('status', 'delivered')
      .gte('created_at', rangeStart.toISOString());

    const rows = data || [];

    // Bucket rows into weeks (0 = current week, 1 = last week, ...)
    const buckets = Array.from({ length: WEEKS }, () => ({ earnings: 0, deliveries: 0, dailyEarnings: [0, 0, 0, 0, 0, 0, 0], dailyDeliveries: [0, 0, 0, 0, 0, 0, 0] }));
    rows.forEach((r: any) => {
      const d = new Date(r.completed_at || r.created_at);
      const weekIdx = Math.max(0, Math.min(WEEKS - 1, Math.floor((currentWeekStart.getTime() - d.getTime() + 1) / (7 * 86400000))));
      const fee = Number(r.delivery_fee || 0);
      const earnings = isFreelance ? fee * 0.7 : fee;
      buckets[weekIdx].earnings += earnings;
      buckets[weekIdx].deliveries += 1;
      if (weekIdx === 0) {
        const dow = d.getUTCDay();
        const di = dow === 0 ? 6 : dow - 1; // Monday = 0
        buckets[0].dailyEarnings[di] += earnings;
        buckets[0].dailyDeliveries[di] += 1;
      }
    });

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    setWeekStats({ earnings: buckets[0].earnings, deliveries: buckets[0].deliveries });
    setDailyWeek(dayNames.map((label, i) => ({
      label,
      earnings: buckets[0].dailyEarnings[i],
      deliveries: buckets[0].dailyDeliveries[i],
    })));

    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const past = [];
    for (let w = 1; w < WEEKS; w++) {
      if (buckets[w].deliveries === 0) continue;
      const wStart = new Date(currentWeekStart);
      wStart.setUTCDate(wStart.getUTCDate() - w * 7);
      const wEnd = new Date(wStart);
      wEnd.setUTCDate(wEnd.getUTCDate() + 6);
      past.push({
        label: `${fmt(wStart)} – ${fmt(wEnd)}`,
        earnings: buckets[w].earnings,
        deliveries: buckets[w].deliveries,
      });
    }
    setPastWeeks(past);
  }, [user, isFreelance]);

  const fetchDriverProfile = useCallback(async () => {
    if (!user) return;
    const { data: profileData } = await supabase
      .from('profiles')
      .select('full_name, email, phone')
      .eq('id', user.id)
      .maybeSingle();

    const { data: applicationData } = await supabase
      .from('driver_applications')
      .select('phone_number, license_number, vehicle_type')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profileData) {
      setDriverProfile({
        full_name: profileData.full_name || '',
        email: profileData.email || user.email || '',
        phone_number: applicationData?.phone_number || profileData.phone || '',
        license_number: applicationData?.license_number || '',
        vehicle_type: applicationData?.vehicle_type || '',
      });
    }
  }, [user]);

  const [ratingDistribution, setRatingDistribution] = useState<number[]>([0, 0, 0, 0, 0]);
  const [recentComments, setRecentComments] = useState<{ comment: string; rating: number; date: string }[]>([]);

  const fetchDriverRatings = useCallback(async () => {
    if (!user) return;
    const { data: ratings } = await supabase
      .from('ratings')
      .select('driver_rating, driver_comment, created_at')
      .eq('driver_id', user.id)
      .not('driver_rating', 'is', null)
      .order('created_at', { ascending: false });

    if (ratings && ratings.length > 0) {
      const sum = ratings.reduce((acc, r) => acc + (r.driver_rating || 0), 0);
      setDriverRatings({
        averageRating: Math.round((sum / ratings.length) * 10) / 10,
        totalRatings: ratings.length,
      });
      const dist = [0, 0, 0, 0, 0];
      ratings.forEach((r) => {
        const v = Math.round(r.driver_rating || 0);
        if (v >= 1 && v <= 5) dist[5 - v] += 1;
      });
      setRatingDistribution(dist);
      setRecentComments(
        ratings
          .filter((r) => r.driver_comment && r.driver_comment.trim().length > 0)
          .slice(0, 5)
          .map((r) => ({ comment: r.driver_comment as string, rating: r.driver_rating || 0, date: r.created_at }))
      );
    } else {
      setDriverRatings({ averageRating: 0, totalRatings: 0 });
      setRatingDistribution([0, 0, 0, 0, 0]);
      setRecentComments([]);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadDriverStatus();
    fetchDriverOrders();
    fetchTodayStats();
    fetchWeekStats();
    fetchDriverProfile();
    fetchDriverRatings();
  }, [user, loadDriverStatus, fetchDriverOrders, fetchTodayStats, fetchWeekStats, fetchDriverProfile, fetchDriverRatings]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  // Realtime: assigned orders
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('driver-orders-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `driver_id=eq.${user.id}` }, () => {
        fetchDriverOrders();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchDriverOrders]);

  // Realtime + polling: unassigned offers for freelance drivers
  useEffect(() => {
    if (!isFreelance || !isOnline) return;
    const channel = supabase
      .channel('driver-offers-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
        fetchOffers();
      })
      .subscribe();
    const poll = window.setInterval(fetchOffers, 20000);
    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [isFreelance, isOnline, fetchOffers]);

  // ---------- Actions ----------
  const toggleOnlineStatus = async () => {
    if (!user) return;
    const newStatus = !isOnline;

    const { error } = await supabase
      .from('driver_profiles')
      .upsert({ user_id: user.id, is_active: newStatus }, { onConflict: 'user_id' });
    if (error) {
      console.error('Error updating status:', error);
      return;
    }

    setIsOnline(newStatus);

    if (newStatus) {
      if (!isFreelance) {
        try {
          const { data: assignedCount } = await supabase.rpc('assign_pending_orders_to_driver', {
            driver_user_id: user.id,
          });
          if (assignedCount && assignedCount > 0) {
            toast({ title: 'Orders Assigned!', description: `You've been assigned ${assignedCount} pending order${assignedCount > 1 ? 's' : ''}` });
          }
        } catch (err) {
          console.error('assign_pending_orders_to_driver failed:', err);
        }
      }
      toast({
        title: "You're now online",
        description: isFreelance ? 'Nearby orders will show up for you to accept' : "You'll automatically receive orders",
      });
      setTimeout(() => { fetchDriverOrders(); fetchOffers(); }, 1000);
    } else {
      toast({ title: "You're now offline", description: 'You won\'t receive new orders' });
      setOffers([]);
    }
  };

  const handleAcceptOffer = async (orderId: string) => {
    if (!user) return;
    setAccepting(true);
    try {
      // Order already assigned to this driver (auto-assignment) — just confirm it.
      if (currentOrder?.id === orderId && currentOrder.driver_id === user.id) {
        setAcceptedIds((prev) => [...prev, orderId]);
        setShowOrderCard(true);
        toast({ title: 'Order accepted!', description: 'Head to the restaurant for pickup.' });
        return;
      }

      const { data, error } = await supabase
        .from('orders')
        .update({ driver_id: user.id })
        .eq('id', orderId)
        .is('driver_id', null)
        .select('id');

      if (error) throw error;

      if (!data || data.length === 0) {
        toast({ title: 'Order already taken', description: 'Another driver accepted this order first.', variant: 'destructive' });
        setOffers((prev) => prev.filter((o) => o.id !== orderId));
        return;
      }

      toast({ title: 'Order accepted!', description: 'Head to the restaurant for pickup.' });
      setAcceptedIds((prev) => [...prev, orderId]);
      setOffers((prev) => prev.filter((o) => o.id !== orderId));
      setShowOrderCard(true);
      fetchDriverOrders();
    } catch (err: any) {
      console.error('Error accepting order:', err);
      toast({ title: 'Could not accept order', description: err?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineOffer = async (orderId: string) => {
    setDeclinedIds((prev) => [...prev, orderId]);

    // If the order was auto-assigned to this driver, release it back to the pool.
    if (user && currentOrder?.id === orderId && currentOrder.driver_id === user.id) {
      const { error } = await supabase
        .from('orders')
        .update({ driver_id: null })
        .eq('id', orderId)
        .eq('driver_id', user.id);
      if (error) {
        console.error('Error declining assigned order:', error);
        toast({ title: 'Could not decline order', description: error.message, variant: 'destructive' });
        return;
      }
      setCurrentOrder(null);
      fetchDriverOrders();
      fetchOffers();
    }
  };


  const handlePickup = async () => {
    if (!currentOrder || !user) return;
    if (!['confirmed', 'ready_for_pickup', 'preparing'].includes(currentOrder.status)) {
      toast({ title: 'Cannot Update', description: `Order is in '${currentOrder.status}' status and cannot be picked up yet.` });
      return;
    }
    const { error } = await supabase
      .from('orders')
      .update({ status: 'picked_up' })
      .eq('id', currentOrder.id)
      .eq('driver_id', user.id);
    if (error) {
      toast({ title: 'Error', description: `Failed to update order status: ${error.message}` });
      return;
    }
    toast({ title: 'Order Picked Up!', description: 'Now navigate to customer location' });
    fetchDriverOrders();
  };

  const handleDropOff = async () => {
    if (!currentOrder || !user) return;
    const { error } = await supabase
      .from('orders')
      .update({ status: 'delivered', completed_at: new Date().toISOString() })
      .eq('id', currentOrder.id)
      .eq('driver_id', user.id);
    if (error) {
      toast({ title: 'Error', description: `Failed to complete delivery: ${error.message}` });
      return;
    }
    toast({ title: 'Order Delivered!', description: 'Great job!' });

    if (!isFreelance) {
      try {
        const { data: assignedCount } = await supabase.rpc('assign_pending_orders_to_driver', { driver_user_id: user.id });
        if (assignedCount && assignedCount > 0) {
          toast({ title: 'New Orders Assigned!', description: `You've been assigned ${assignedCount} new order${assignedCount > 1 ? 's' : ''}` });
        }
      } catch (err) {
        console.error('assign_pending_orders_to_driver failed:', err);
      }
    }

    fetchDriverOrders();
    setTimeout(() => { fetchTodayStats(); fetchWeekStats(); }, 1000);
  };

  const handleNavigate = (destination: 'pickup' | 'dropoff') => {
    if (!currentOrder) return;
    const address = destination === 'pickup' ? currentOrder.restaurants?.address : currentOrder.delivery_address;
    if (address) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`, '_blank');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }
  if (!user) return null;

  const visibleOffer = offers.find((o) => !declinedIds.includes(o.id)) || null;

  // Freelance drivers must confirm any order that landed on them before they start it
  const pendingAssignedOrder =
    isFreelance &&
    currentOrder &&
    currentOrder.status !== 'picked_up' &&
    !acceptedIds.includes(currentOrder.id)
      ? currentOrder
      : null;

  const isOrderPaid = (order: Order): boolean =>
    ['paid', 'completed', 'success', 'successful'].includes(
      String(order.payment_status || '').toLowerCase()
    ) ||
    (String(order.payment_method || '').toLowerCase().includes('wave') &&
      !['pending', 'failed', 'cancelled'].includes(
        String(order.payment_status || '').toLowerCase()
      ));

  const toOffer = (order: Order): DeliveryOffer => ({
    id: order.id,
    restaurantName: order.restaurants?.name || 'Restaurant',
    restaurantAddress: order.restaurants?.address,
    deliveryAddress: order.delivery_address,
    payout: Number(order.delivery_fee || 0) * 0.7,
    orderTotal: Number(order.total_amount || 0),
    deliveryFee: Number(order.delivery_fee || 0),
    isPaid: isOrderPaid(order),


    items: offerItems[order.id],
    customerName: order.customer_name,
    notes: order.notes,
  });

  // Freelance offer popup takes priority; otherwise show assigned order details
  const popup = (() => {
    if (!isOnline) return null;
    const pending = pendingAssignedOrder || (!currentOrder ? visibleOffer : null);
    if (isFreelance && pending) {
      return (
        <DeliveryOfferPopup
          offer={toOffer(pending)}
          canDecide
          accepting={accepting}
          onAccept={handleAcceptOffer}
          onDecline={handleDeclineOffer}
        />
      );
    }
    return null;
  })();


  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {tab === 'drive' ? (
        <div className="fixed inset-0 bottom-[76px] z-0 isolate overflow-hidden">
          <DriverMap
            currentOrder={currentOrder}
            queuedOrders={queuedOrders}
            driverLocation={driverLocation}
            onNavigate={handleNavigate}
          />

          {/* Online toggle floating on the map */}
          <div className="absolute z-[1200] top-4 left-4 right-4 flex items-center justify-between gap-3 pointer-events-none">
            <Button
              variant="secondary"
              size="icon"
              className="pointer-events-auto bg-white shadow-lg rounded-full"
              onClick={() => navigate('/home')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            {!popup && (
              <Button
                onClick={toggleOnlineStatus}
                className={`pointer-events-auto rounded-full shadow-lg font-semibold ${
                  isOnline ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full mr-2 ${isOnline ? 'bg-green-500' : 'bg-white'}`} />
                {isOnline ? 'Online' : 'Go Online'}
              </Button>
            )}
          </div>

          {/* Assigned order sheet (fleet + accepted freelance orders) */}
          {isOnline && currentOrder && showOrderCard && !pendingAssignedOrder && (
            <div className="absolute z-[1200] bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">
                    {currentOrder.status === 'picked_up' ? 'Deliver to customer' : 'Pick up order'}
                  </p>
                  <h3 className="text-lg font-bold truncate">
                    {currentOrder.status === 'picked_up'
                      ? currentOrder.customer_name || 'Customer'
                      : currentOrder.restaurants?.name || 'Restaurant'}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold">
                    D{Number(currentOrder.total_amount || 0).toFixed(2)}
                    {isFreelance && (
                      <span className="text-base text-red-600">
                        {' '}
                        (D{(Number(currentOrder.delivery_fee || 0) * 0.7).toFixed(2)})
                      </span>
                    )}
                  </span>
                  {(isOrderPaid(currentOrder)) && (
                    <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">
                      PAID
                    </span>
                  )}
                </div>
              </div>


              <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                {currentOrder.status === 'picked_up'
                  ? currentOrder.delivery_address
                  : currentOrder.restaurants?.address || currentOrder.delivery_address}
              </p>

              {/* Customer contact */}
              {(currentOrder.customer_name || currentOrder.customer_phone) && (
                <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl bg-gray-50 p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {currentOrder.customer_name || 'Customer'}
                    </p>
                    {currentOrder.customer_phone && (
                      <p className="text-xs text-gray-500">{currentOrder.customer_phone}</p>
                    )}
                  </div>
                  {currentOrder.customer_phone && (
                    <a
                      href={`tel:${currentOrder.customer_phone.replace(/\s+/g, '')}`}
                      className="shrink-0 inline-flex items-center gap-1.5 bg-red-600 text-white text-sm font-bold px-4 py-2.5 rounded-full active:scale-95 transition-transform"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </a>
                  )}
                </div>
              )}

              {queuedOrders.length > 0 && (
                <div className="mb-3 text-xs font-medium text-gray-500">
                  {queuedOrders.length} more order{queuedOrders.length > 1 ? 's' : ''} in your queue
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 h-12 rounded-full font-semibold"
                  onClick={() => handleNavigate(currentOrder.status === 'picked_up' ? 'dropoff' : 'pickup')}
                >
                  <Navigation className="h-4 w-4 mr-2" />
                  Navigate
                </Button>
                <Button
                  className="flex-1 h-12 rounded-full font-bold bg-red-600 hover:bg-red-700 text-white"
                  onClick={currentOrder.status === 'picked_up' ? handleDropOff : handlePickup}
                >
                  <Package className="h-4 w-4 mr-2" />
                  {currentOrder.status === 'picked_up' ? 'Delivered' : 'Picked up'}
                </Button>
              </div>
            </div>
          )}

          {/* Idle state */}
          {isOnline && !currentOrder && !visibleOffer && (
            <div className="absolute z-[1200] bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl p-6 text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <h3 className="font-semibold">Waiting for orders</h3>
              <p className="text-sm text-gray-600">
                {isFreelance ? 'New offers will pop up here' : "You'll be assigned orders automatically"}
              </p>
            </div>
          )}

          {!isOnline && (
            <div className="absolute z-[1200] bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl p-6 text-center">
              <Car className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <h3 className="font-semibold mb-1">You're offline</h3>
              <p className="text-sm text-gray-600 mb-4">Go online to start receiving orders</p>
              <Button onClick={toggleOnlineStatus} className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold">
                Go Online
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/home')}>
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-2xl font-bold capitalize">{tab}</h1>
          </div>

          {tab === 'account' && (
            <>
              {/* Profile hero */}
              <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-3xl p-6 text-white shadow-lg shadow-red-600/20">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-2xl font-extrabold border-2 border-white/30">
                    {(driverProfile?.full_name || 'D').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-bold truncate">{driverProfile?.full_name || 'Driver'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] font-semibold bg-white/20 rounded-full px-2.5 py-0.5">
                        {isFreelance ? 'Freelance' : 'Fleet'}
                      </span>
                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-white/80">
                        <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400' : 'bg-white/40'}`} />
                        {isOnline ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={toggleOnlineStatus}
                  className="w-full h-11 rounded-full bg-white text-red-700 hover:bg-white/90 font-bold"
                >
                  {isOnline ? 'Go Offline' : 'Go Online'}
                </Button>
              </div>

              {/* Today's performance */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center mb-2">
                    <Package className="h-5 w-5 text-red-600" />
                  </div>
                  <p className="text-2xl font-extrabold">{todayStats.deliveries}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Deliveries today</p>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center mb-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-extrabold">{todayStats.onTimeRate}%</p>
                  <p className="text-xs text-gray-500 mt-0.5">On-time rate</p>
                </div>
              </div>

              {/* Driver information */}
              {driverProfile && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-5 pt-4 pb-3">
                    <h3 className="font-bold">Driver Information</h3>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 -mr-2" onClick={() => navigate('/edit-profile')}>
                      <Edit className="h-4 w-4 mr-1.5" />
                      Edit
                    </Button>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {[
                      { icon: User, label: 'Full Name', value: driverProfile.full_name },
                      { icon: Mail, label: 'Email', value: driverProfile.email },
                      { icon: Phone, label: 'Phone Number', value: driverProfile.phone_number },
                      { icon: CreditCard, label: 'License Number', value: driverProfile.license_number },
                      { icon: Bike, label: 'Vehicle Type', value: driverProfile.vehicle_type },
                    ].map(({ icon: Icon, label, value }) => (
                      <div key={label} className="flex items-center gap-3 px-5 py-3.5">
                        <div className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center shrink-0">
                          <Icon className="h-4 w-4 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">{label}</p>
                          <p className="text-sm font-medium truncate">{value || 'Not set'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'ratings' && (
            <>
              {/* Rating hero */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-5xl font-extrabold tracking-tight">
                      {driverRatings.totalRatings > 0 ? driverRatings.averageRating.toFixed(1) : '—'}
                    </p>
                    <div className="flex items-center gap-0.5 mt-1.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`h-4 w-4 ${
                            s <= Math.round(driverRatings.averageRating)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-200 fill-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">
                      {driverRatings.totalRatings} rating{driverRatings.totalRatings === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {ratingDistribution.map((count, i) => {
                      const star = 5 - i;
                      const pct = driverRatings.totalRatings > 0 ? (count / driverRatings.totalRatings) * 100 : 0;
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500 w-3 text-right">{star}</span>
                          <Star className="h-3 w-3 text-yellow-400 fill-yellow-400 shrink-0" />
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-6">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center mb-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-extrabold">{todayStats.onTimeRate}%</p>
                  <p className="text-xs text-gray-500 mt-0.5">On-time rate</p>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center mb-2">
                    <Package className="h-5 w-5 text-red-600" />
                  </div>
                  <p className="text-2xl font-extrabold">{weekStats.deliveries}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Deliveries this week</p>
                </div>
              </div>

              {/* Recent feedback */}
              {recentComments.length > 0 && (
                <div>
                  <h3 className="font-bold mb-2 px-1">Recent Feedback</h3>
                  <div className="space-y-3">
                    {recentComments.map((c, i) => (
                      <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star
                                key={s}
                                className={`h-3.5 w-3.5 ${
                                  s <= c.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(c.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <div className="flex gap-2.5">
                          <MessageSquare className="h-4 w-4 text-gray-300 shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-700">{c.comment}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {driverRatings.totalRatings === 0 && (
                <div className="text-center py-10">
                  <Star className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                  <p className="font-semibold text-gray-700">No ratings yet</p>
                  <p className="text-sm text-gray-500 mt-1">Complete deliveries to start receiving customer ratings</p>
                </div>
              )}
            </>
          )}

          {tab === 'earnings' && (
            <div>
              {/* Centered headline */}
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 mb-1">This week</p>
                {isFreelance ? (
                  <>
                    <p className="text-5xl font-extrabold tracking-tight mb-2">D{weekStats.earnings.toFixed(2)}</p>
                    <p className="text-sm text-gray-600">
                      From {weekStats.deliveries} completed deliver{weekStats.deliveries === 1 ? 'y' : 'ies'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-5xl font-extrabold tracking-tight mb-2">{weekStats.deliveries}</p>
                    <p className="text-sm text-gray-600">Orders completed this week</p>
                  </>
                )}
              </div>

              {/* Current week graph */}
              <Card>
                <CardContent className="p-4">
                  {(() => {
                    const values = dailyWeek.map((d) => (isFreelance ? d.earnings : d.deliveries));
                    const max = Math.max(...values, 1);
                    const todayIdx = (() => { const dow = new Date().getUTCDay(); return dow === 0 ? 6 : dow - 1; })();
                    return (
                      <>
                        <div className="flex items-end gap-2 h-32">
                          {dailyWeek.map((d, i) => {
                            const v = values[i];
                            const heightPct = Math.max(4, (v / max) * 100);
                            return (
                              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full">
                                {v > 0 && (
                                  <span className="text-[10px] font-semibold text-gray-500">
                                    {isFreelance ? `D${v.toFixed(0)}` : v}
                                  </span>
                                )}
                                <div
                                  className={`w-full max-w-[32px] rounded-t-lg transition-all duration-500 ${
                                    i === todayIdx ? 'bg-red-600' : v > 0 ? 'bg-red-300' : 'bg-gray-100'
                                  }`}
                                  style={{ height: `${heightPct}%`, minHeight: '4px' }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex gap-2 mt-2">
                          {dailyWeek.map((d, i) => (
                            <div key={i} className={`flex-1 text-center text-xs ${i === todayIdx ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                              {d.label}
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Earnings history */}
              <div className="mt-6">
                <h3 className="text-lg font-bold mb-1">Earnings History</h3>
                {pastWeeks.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No previous weeks yet.</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {pastWeeks.map((w, i) => (
                      <div key={i} className="flex items-center justify-between py-4">
                        <div>
                          <p className="font-medium">{w.label}</p>
                          <p className="text-xs text-gray-500">
                            {w.deliveries} deliver{w.deliveries === 1 ? 'y' : 'ies'}
                          </p>
                        </div>
                        <span className="font-bold">
                          {isFreelance ? `D${w.earnings.toFixed(2)}` : w.deliveries}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {popup}

      <DriverBottomNav active={tab} onChange={(t) => { setTab(t); setShowOrderCard(true); }} />
    </div>
  );
};

export default DriverDashboardPage;
